/**
 * When a treatment step is expected to happen, and which date the outcome step
 * should speak from once real ones exist.
 *
 * Moved out of `CycleDetailScreen` so it can be asserted. It could not be
 * before, which is how the `outcome` step came to have no entry at all: absent
 * from the table below, a step falls to `[0, 0]` and both ends of its window
 * land on the day the cycle started. The badge then reads "Est. <cycle start>"
 * on a step that happens last. That is not a visible no-op — it is a confident,
 * wrong date.
 */

export const DAY_IN_MS = 86400000;

/**
 * Day offsets from the start of an ovarian-stimulation cycle.
 *
 * `outcome` sits at the tail of luteal support rather than alongside it: a beta
 * is drawn roughly 10–14 days after a transfer, and the transfer is day 17–18.
 * Luteal support keeps its full 18–32 because the medication really does run
 * across the whole stretch — the two overlap in life, and should here.
 */
const OVARIAN_OFFSETS: Record<string, [number, number]> = {
  pre_treatment: [0, 3],
  protocol: [4, 14],
  stimulation: [4, 14],
  trigger_shot: [10, 12],
  egg_retrieval: [11, 14],
  sperm_collection: [11, 11],
  freezing: [11, 15],
  fertilization: [12, 12],
  embryo_development: [13, 17],
  transfer: [17, 18],
  luteal_phase: [18, 32],
  outcome: [28, 32],
  // Two pre-existing gaps of the same kind, both found by the drift guard
  // below — each read the cycle's start date on a step that happens much later.
  //
  // IUI's central procedure, placed with the sperm collection it happens
  // alongside.
  insemination: [11, 12],
  // Biopsy at the end of embryo development, then the wait for results. Both
  // numbers are display estimates like every other row here, not a clinical
  // claim about any particular lab.
  pgt_testing: [16, 24],
};

const SPERM_OFFSETS: Record<string, [number, number]> = {
  pre_treatment: [0, 6],
  screening: [7, 10],
  sperm_collection: [11, 11],
  analysis: [12, 14],
  freezing: [12, 15],
};

/**
 * Steps measured from the retrieval rather than from the start of the cycle.
 *
 * Arithmetically this is a no-op today — the anchor is cycle start plus eleven
 * days and the branch subtracts eleven again — so it is a seam kept open for a
 * real retrieval date, not a behaviour. `outcome` belongs in it for the same
 * reason its neighbours do.
 */
const RETRIEVAL_ANCHORED = new Set([
  "fertilization",
  "embryo_development",
  "transfer",
  "luteal_phase",
  "outcome",
]);

export function estimateTimelineWindow(params: {
  stepId: string;
  cycleType: string;
  cycleStartMs: number;
}): { startMs: number; endMs: number } {
  const { stepId, cycleType, cycleStartMs } = params;
  const retrievalStartMs = cycleStartMs + 11 * DAY_IN_MS;

  if (cycleType === "sperm_freezing") {
    const offset = SPERM_OFFSETS[stepId] ?? [0, 0];
    return {
      startMs: cycleStartMs + offset[0] * DAY_IN_MS,
      endMs: cycleStartMs + offset[1] * DAY_IN_MS,
    };
  }

  const offset = OVARIAN_OFFSETS[stepId] ?? [0, 0];
  if (RETRIEVAL_ANCHORED.has(stepId)) {
    return {
      startMs: retrievalStartMs + (offset[0] - 11) * DAY_IN_MS,
      endMs: retrievalStartMs + (offset[1] - 11) * DAY_IN_MS,
    };
  }
  return {
    startMs: cycleStartMs + offset[0] * DAY_IN_MS,
    endMs: cycleStartMs + offset[1] * DAY_IN_MS,
  };
}

/**
 * Whether a step has a place in the cycle at all, or is only falling back.
 *
 * Exposed for the drift guard: `[0, 0]` is indistinguishable from a real answer
 * once it has been turned into a date, so the test asks the question here
 * instead.
 */
export function hasEstimatedWindow(stepId: string, cycleType: string): boolean {
  const table =
    cycleType === "sperm_freezing" ? SPERM_OFFSETS : OVARIAN_OFFSETS;
  return Object.prototype.hasOwnProperty.call(table, stepId);
}

/**
 * Which date the outcome step should speak from.
 *
 * The outcome step borrows the luteal phase's API row, so left to the ordinary
 * path its badge repeats the luteal phase's dates word for word — two rows, two
 * names, one set of dates, which reads as a rendering fault rather than as
 * information. It has better dates of its own available.
 *
 * Order is what she knows, most settled first: a result she has recorded beats a
 * test date she has only scheduled, and either beats an estimate.
 */
export function resolveOutcomeTiming(params: {
  /** `cycle.outcome.dateRecorded` — the day a result was recorded, if any. */
  recordedOn?: string | null;
  /**
   * The pregnancy test date she has set. Pass the value the wait already
   * resolves, not the luteal phase's `endDate`: on a FET the test date is the
   * typed beta order date and that `endDate` is not it.
   */
  testDate?: string | null;
}): { kind: "recorded" | "test"; date: string } | { kind: "estimate" } {
  if (params.recordedOn) return { kind: "recorded", date: params.recordedOn };
  if (params.testDate) return { kind: "test", date: params.testDate };
  return { kind: "estimate" };
}
